import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '@/stores/user'

const routes = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/Login.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    name: 'dashboard',
    component: () => import('@/views/Dashboard.vue'),
  },
  {
    path: '/invoices',
    name: 'invoices',
    component: () => import('@/views/InvoiceList.vue'),
    meta: { adminOnly: true },
  },
  {
    path: '/invoices/new',
    name: 'invoices-new',
    component: () => import('@/views/InvoiceForm.vue'),
    meta: { adminOnly: true },
  },
  {
    path: '/invoices/:id(\\d+)',
    name: 'invoices-edit',
    component: () => import('@/views/InvoiceForm.vue'),
    meta: { adminOnly: true },
  },
  {
    path: '/vehicles',
    name: 'vehicles',
    component: () => import('@/views/VehicleList.vue'),
  },
  {
    path: '/vehicles/new',
    name: 'vehicles-new',
    component: () => import('@/views/VehicleForm.vue'),
  },
  {
    path: '/vehicles/:id(\\d+)',
    name: 'vehicles-edit',
    component: () => import('@/views/VehicleForm.vue'),
  },
  {
    path: '/mileage',
    name: 'mileage',
    component: () => import('@/views/MileageList.vue'),
  },
  {
    path: '/mileage/new',
    name: 'mileage-new',
    component: () => import('@/views/MileageForm.vue'),
  },
  {
    path: '/mileage/destinations',
    name: 'mileage-destinations',
    component: () => import('@/views/Vehicle/MileageDestinations.vue'),
  },
  {
    path: '/mileage/:id(\\d+)',
    name: 'mileage-edit',
    component: () => import('@/views/MileageForm.vue'),
  },
  {
    path: '/orders',
    name: 'orders',
    component: () => import('@/views/OrderList.vue'),
  },
  {
    path: '/orders/history',
    name: 'orders-history',
    component: () => import('@/views/OrderHistory.vue'),
  },
  {
    path: '/valmisruoka',
    name: 'valmisruoka',
    component: () => import('@/views/Valmisruoka.vue'),
  },
  {
    path: '/users',
    name: 'users',
    component: () => import('@/views/User/UserList.vue'),
    meta: { adminOnly: true },
  },
  // ===== Phase 2.2 后台管理（菜品 / 营业时间 / 周午餐表） =====
  {
    path: '/admin/menu',
    name: 'admin-menu',
    component: () => import('@/views/MenuManagement.vue'),
    meta: { requireRoles: ['admin', 'laukaa_manager', 'siilinjarvi_manager'] },
  },
  {
    path: '/admin/business-hours',
    name: 'admin-business-hours',
    component: () => import('@/views/BusinessHoursEditor.vue'),
    meta: { requireRoles: ['admin', 'laukaa_manager', 'siilinjarvi_manager'] },
  },
  {
    path: '/admin/lounaslista',
    name: 'admin-lounaslista',
    component: () => import('@/views/WeeklyLunchEditor.vue'),
    meta: {
      requireRoles: [
        'admin',
        'laukaa_manager',
        'siilinjarvi_manager',
        'content_editor',
      ],
    },
  },
  // ===== Phase 2.2 Block 3d-2 后台管理（CMS / 合作店 / B2B 产品） =====
  {
    path: '/admin/cms',
    name: 'admin-cms',
    component: () => import('@/views/CMSEditor.vue'),
    meta: { requireRoles: ['admin', 'content_editor'] },
  },
  {
    path: '/admin/partners',
    name: 'admin-partners',
    component: () => import('@/views/PartnersManagement.vue'),
    meta: { requireRoles: ['admin', 'content_editor'] },
  },
  {
    path: '/admin/kitchen-products',
    name: 'admin-kitchen',
    component: () => import('@/views/KitchenProductsManagement.vue'),
    meta: { requireRoles: ['admin', 'content_editor'] },
  },
  // Phase A — 门店配置（buffet 价格 / hero 图 / 儿童折扣等）— admin only
  {
    path: '/admin/restaurants',
    name: 'admin-restaurants',
    component: () => import('@/views/RestaurantsManagement.vue'),
    meta: { adminOnly: true },
  },
  {
    path: '/change-password',
    name: 'change-password',
    component: () => import('@/views/User/ChangePassword.vue'),
  },

  // ===== C8 · Omavalvonta 温度 / pH 台账 =====
  // /omv 根路由按角色重定向：driver → /omv/driver, kitchen → /omv/sushi, admin → /omv/anomalies
  // 路由守卫里 to.name === 'omv-root' 时会动态算目标（见下方 beforeEach）。
  {
    path: '/omv',
    name: 'omv-root',
    redirect: { name: 'omv-anomalies' }, // 兜底，beforeEach 里覆盖
  },
  {
    path: '/omv/sushi',
    name: 'omv-sushi',
    component: () => import('@/views/omv/Sushi.vue'),
  },
  {
    path: '/omv/hot',
    name: 'omv-hot',
    component: () => import('@/views/omv/Hot.vue'),
  },
  {
    path: '/omv/equipment-log',
    name: 'omv-eqlog',
    component: () => import('@/views/omv/EquipmentLog.vue'),
  },
  {
    path: '/omv/anomalies',
    name: 'omv-anomalies',
    component: () => import('@/views/omv/Anomalies.vue'),
  },
  {
    path: '/omv/equipment',
    name: 'omv-equipment',
    component: () => import('@/views/omv/Equipment.vue'),
    meta: { adminOnly: true },
  },
  {
    path: '/omv/reports',
    name: 'omv-reports',
    component: () => import('@/views/omv/Reports.vue'),
  },
  {
    path: '/omv/batch',
    name: 'omv-batch',
    component: () => import('@/views/omv/Batch.vue'),
    meta: { adminOnly: true },
  },
  {
    path: '/omv/driver',
    name: 'omv-driver',
    component: () => import('@/views/omv/Driver.vue'),
  },

  // C9 · 超市档案 + 配送路线（admin only）
  {
    path: '/omv/supermarkets',
    name: 'omv-supermarkets',
    component: () => import('@/views/omv/Supermarkets.vue'),
    meta: { adminOnly: true },
  },
  {
    path: '/omv/routes',
    name: 'omv-routes',
    component: () => import('@/views/omv/Routes.vue'),
    meta: { adminOnly: true },
  },

  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// 路由守卫：受保护路由需登录；管理员路由需 admin
router.beforeEach(async (to) => {
  const store = useUserStore()
  if (to.meta.public) {
    if (store.isLoggedIn && to.name === 'login') return { name: 'dashboard' }
    return true
  }
  if (!store.isLoggedIn) return { name: 'login', query: { redirect: to.fullPath } }
  if (!store.user) {
    try {
      await store.fetchMe()
    } catch {
      store.logout()
      return { name: 'login' }
    }
  }
  // 管理员专属路由：非 admin 直接回仪表盘
  if (to.meta.adminOnly && !store.isAdmin) {
    return { name: 'dashboard' }
  }
  // 角色白名单（Phase 2.2 后台路由）：role 不在 requireRoles 列表里直接回仪表盘
  if (
    Array.isArray(to.meta.requireRoles) &&
    to.meta.requireRoles.length > 0 &&
    !to.meta.requireRoles.includes(store.user?.role)
  ) {
    return { name: 'dashboard' }
  }
  // 强制改密：用户被标记 must_reset_password 时，只能去 /change-password
  if (store.mustResetPassword && to.name !== 'change-password') {
    return { name: 'change-password' }
  }
  // C8 · Omavalvonta 根路由按角色重定向（driver 优先级最高，因为送货员只用得上 /omv/driver）
  if (to.name === 'omv-root') {
    if (store.isDriver && !store.isAdmin) return { name: 'omv-driver' }
    if (store.isKitchen) return { name: 'omv-sushi' }
    return { name: 'omv-anomalies' } // admin 默认看异常清单总览
  }
  return true
})

export default router
